import React, { useEffect, useState, useCallback } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { usePage } from '../../hooks/usePage';
import {
  analyticsApi,
  AnalyticsOverview,
  UserGrowthPoint,
  TransactionVolumePoint,
  ReminderOutcomes,
} from '../../api/analytics';
import { StatCard } from '../../components/ui/StatCard';
import {
  Users,
  Star,
  Banknote,
  CheckCircle,
  UserPlus,
  CalendarDays,
  ArrowLeftRight,
  ThumbsUp,
  Send,
  Eye,
  BellOff,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import {
  DateRangeFilter,
  DateRange,
  DateRangePreset,
  computeRange,
} from '../../components/ui/DateRangeFilter';

const formatNaira = (n: number) =>
  `₦${n.toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;

const formatCompact = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
};

const COLORS = {
  blue: '#12A6D4',
  green: '#209347',
  purple: '#9D5FDB',
  neutral: '#566472',
  light: '#E0E4E8',
  grid: '#F7F7F7',
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
  formatter?: (value: number) => string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  formatter,
}) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: 'var(--neutral-100)',
        border: '1px solid var(--neutral-300)',
        borderRadius: 'var(--radius-md)',
        padding: '10px 14px',
        boxShadow: 'var(--shadow-md)',
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: 600, color: 'var(--neutral-800)', marginBottom: 6 }}>
        {label}
      </div>
      {payload.map((p, i) => (
        <div
          key={i}
          style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--neutral-600)' }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: p.color,
              display: 'inline-block',
            }}
          />
          <span>{p.name}:</span>
          <span style={{ fontWeight: 600, color: 'var(--neutral-800)' }}>
            {formatter ? formatter(p.value) : p.value.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
};

export const Dashboard: React.FC = () => {
  usePage('Dashboard');

  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [growth, setGrowth] = useState<UserGrowthPoint[]>([]);
  const [volumes, setVolumes] = useState<TransactionVolumePoint[]>([]);
  const [reminders, setReminders] = useState<ReminderOutcomes | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(() =>
    computeRange('6_months'),
  );

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        setOverviewLoading(true);
        const data = await analyticsApi.getOverview();
        setOverview(data);
      } catch (err) {
        setError('Failed to load dashboard data. Please refresh.');
      } finally {
        setOverviewLoading(false);
      }
    };
    void fetchOverview();
  }, []);

  useEffect(() => {
    const fetchCharts = async () => {
      try {
        setChartsLoading(true);
        const [growthData, volumeData, reminderData] = await Promise.all([
          analyticsApi.getUserGrowth(dateRange.fromDate, dateRange.toDate),
          analyticsApi.getTransactionVolumes(
            dateRange.fromDate,
            dateRange.toDate,
          ),
          analyticsApi.getReminderOutcomes(
            dateRange.fromDate,
            dateRange.toDate,
          ),
        ]);
        setGrowth(growthData);
        setVolumes(volumeData);
        setReminders(reminderData);
      } catch (err) {
        setError('Failed to load chart data. Please try a different date range.');
      } finally {
        setChartsLoading(false);
      }
    };
    void fetchCharts();
  }, [dateRange]);

  const handleRangeChange = useCallback(
    (_preset: DateRangePreset, range: DateRange) => {
      setDateRange(range);
    },
    [],
  );

  if (overviewLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
        <Spinner size={32} />
      </div>
    );
  }

  if (error && !overview) {
    return (
      <Card>
        <p style={{ color: 'var(--danger-600)', textAlign: 'center' }}>{error}</p>
      </Card>
    );
  }

  if (!overview) return null;

  const tierData = [
    { name: 'Tier 0', value: overview.users.byTier.tier0, color: COLORS.neutral },
    { name: 'Tier 1', value: overview.users.byTier.tier1, color: COLORS.blue },
    { name: 'Tier 2', value: overview.users.byTier.tier2, color: COLORS.green },
    { name: 'Tier 3', value: overview.users.byTier.tier3, color: COLORS.purple },
  ].filter((d) => d.value > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Top Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16,
        }}
      >
        <StatCard label="Total Users" value={overview.users.total.toLocaleString()} icon={Users} accent="blue" />
        <StatCard label="Premium Subscribers" value={overview.users.premium.toLocaleString()} icon={Star} accent="purple" />
        <StatCard label="Total Volume" value={formatNaira(overview.transactions.totalVolume)} icon={Banknote} accent="green" />
        <StatCard label="Success Rate" value={`${overview.transactions.successRate}%`} icon={CheckCircle} accent="blue" />
      </div>

      {/* Secondary Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16,
        }}
        >
        <StatCard label="New This Month" value={overview.users.newThisMonth} icon={UserPlus} accent="neutral" />
        <StatCard label="New Today" value={overview.users.newToday} icon={CalendarDays} accent="neutral" />
        <StatCard label="Total Transactions" value={overview.transactions.total.toLocaleString()} icon={ArrowLeftRight} accent="neutral" />
        <StatCard label="Successful Transactions" value={overview.transactions.successful.toLocaleString()} icon={ThumbsUp} accent="neutral" />
        </div>

      {/* Date Range Filter */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--neutral-800)',
              margin: 0,
            }}
          >
            Trends
          </h2>
          <p
            style={{
              fontSize: 13,
              color: 'var(--neutral-500)',
              marginTop: 2,
            }}
          >
            Showing data from {dateRange.fromDate.toLocaleDateString('en-NG')} to{' '}
            {dateRange.toDate.toLocaleDateString('en-NG')}
          </p>
        </div>
        <DateRangeFilter defaultPreset="6_months" onChange={handleRangeChange} />
      </div>

      {/* Charts Row 1 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
          gap: 16,
        }}
      >
        <Card>
          <h3
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--neutral-800)',
              marginBottom: 4,
            }}
          >
            User Growth
          </h3>
          <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginBottom: 20 }}>
            New users registered in selected range
          </p>
          {chartsLoading ? (
            <div
              style={{
                height: 280,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Spinner size={24} />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={growth} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="userGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.blue} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={COLORS.blue} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={COLORS.grid} vertical={false} />
                <XAxis
                  dataKey="label"
                  stroke={COLORS.neutral}
                  style={{ fontSize: 12 }}
                  axisLine={{ stroke: COLORS.light }}
                  tickLine={false}
                />
                <YAxis
                  stroke={COLORS.neutral}
                  style={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="count"
                  name="New Users"
                  stroke={COLORS.blue}
                  strokeWidth={2.5}
                  fill="url(#userGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <h3
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--neutral-800)',
              marginBottom: 4,
            }}
          >
            Users by Tier
          </h3>
          <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginBottom: 20 }}>
            Distribution across verification tiers
          </p>
          {tierData.length === 0 ? (
            <div
              style={{
                height: 280,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--neutral-500)',
                fontSize: 13,
              }}
            >
              No users yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={tierData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                  labelLine={false}
                  style={{ fontSize: 11 }}
                >
                  {tierData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Charts Row 2 */}
      <Card>
        <h3
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--neutral-800)',
            marginBottom: 4,
          }}
        >
          Transaction Volume & Revenue
        </h3>
        <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginBottom: 20 }}>
          Transaction volume and fees collected in selected range
        </p>
        {chartsLoading ? (
          <div
            style={{
              height: 320,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spinner size={24} />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={volumes} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={COLORS.grid} vertical={false} />
              <XAxis
                dataKey="label"
                stroke={COLORS.neutral}
                style={{ fontSize: 12 }}
                axisLine={{ stroke: COLORS.light }}
                tickLine={false}
              />
              <YAxis
                yAxisId="left"
                stroke={COLORS.neutral}
                style={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatCompact}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke={COLORS.neutral}
                style={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatCompact}
              />
              <Tooltip content={<CustomTooltip formatter={formatNaira} />} />
              <Legend
                wrapperStyle={{ fontSize: 12, color: 'var(--neutral-600)' }}
                iconType="circle"
                iconSize={8}
              />
              <Bar
                yAxisId="left"
                dataKey="volume"
                name="Volume"
                fill={COLORS.blue}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                yAxisId="right"
                dataKey="fees"
                name="Fees"
                fill={COLORS.green}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Payment reminders */}
      <div>
        <h2
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--neutral-800)',
            margin: '0 0 2px',
          }}
        >
          Payment reminders
        </h2>
        <p style={{ fontSize: 13, color: 'var(--neutral-500)', margin: '0 0 16px' }}>
          WhatsApp reminder delivery + opt-outs in the selected range
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16,
        }}
      >
        <StatCard
          label="Reminders sent"
          value={(reminders?.requests.totalReminders ?? 0).toLocaleString()}
          icon={Send}
          accent="blue"
        />
        <StatCard
          label="Delivery rate"
          value={`${reminders?.whatsapp.deliveryRate ?? 0}%`}
          icon={CheckCircle}
          accent="green"
        />
        <StatCard
          label="Read rate"
          value={`${reminders?.whatsapp.readRate ?? 0}%`}
          icon={Eye}
          accent="purple"
        />
        <StatCard
          label="Opt-outs (total)"
          value={(reminders?.optOuts.total ?? 0).toLocaleString()}
          icon={BellOff}
          accent="neutral"
        />
      </div>

      <Card>
        <h3
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--neutral-800)',
            marginBottom: 4,
          }}
        >
          WhatsApp delivery funnel
        </h3>
        <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginBottom: 20 }}>
          Each reminded payee counted in the furthest state their message reached
        </p>
        {chartsLoading ? (
          <div
            style={{
              height: 260,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spinner size={24} />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={[
                { label: 'Sent', count: reminders?.whatsapp.sent ?? 0, fill: COLORS.neutral },
                { label: 'Delivered', count: reminders?.whatsapp.delivered ?? 0, fill: COLORS.blue },
                { label: 'Read', count: reminders?.whatsapp.read ?? 0, fill: COLORS.green },
                { label: 'Failed', count: reminders?.whatsapp.failed ?? 0, fill: COLORS.purple },
              ]}
              margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
            >
              <CartesianGrid stroke={COLORS.grid} vertical={false} />
              <XAxis
                dataKey="label"
                stroke={COLORS.neutral}
                style={{ fontSize: 12 }}
                axisLine={{ stroke: COLORS.light }}
                tickLine={false}
              />
              <YAxis
                stroke={COLORS.neutral}
                style={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                tickFormatter={formatCompact}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Payees" radius={[4, 4, 0, 0]}>
                {[COLORS.neutral, COLORS.blue, COLORS.green, COLORS.purple].map(
                  (c, i) => (
                    <Cell key={i} fill={c} />
                  ),
                )}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
};
